import { Box, Button, Chip, Stack, Typography, useMediaQuery } from '@mui/material'
import { LockOpenOutlined, LockOutlined, PublishedWithChangesRounded } from '@mui/icons-material'
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryAnalyticReportService from '../../../shared/services/library-analytic-report/library-analytic-report.service';
import { ILibraryAnalyticReport } from '../../../shared/services/library-analytic-report/library-analytic-report.model';
import { ILibraryPushTarget } from '../../dialogs/partner/partner-library-push-dialog';
import gaiaTheme from '../../../root-components/theme';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { LoadingPage } from '../loading-page';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { PartnerLibraryPushDialog } from '../../dialogs/partner/partner-library-push-dialog';
import { PartnerLibraryDeleteDialog } from '../../dialogs/partner/partner-library-delete-dialog';
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar';
import { useDebounce } from '../../../hooks/use-debounce-hook';

// Which report period a client would end up on, phrased for the push dialog. Shown BEFORE the
// push, so a fallback is never a surprise discovered afterwards.
const periodDetail = (client: ILibraryPushTarget) => {
  const period = client.reportPeriod
  if (!period || period.reason === 'custom-dates') return null
  if (period.reason === 'none') return 'They have no report periods yet'
  if (period.reason === 'exact') return `Will use "${period.name}"`
  if (period.reason === 'ambiguous') return `Will use "${period.name}" (they have more than one period with that name)`
  return `No period with that name, so "${period.name}" will be used instead`
}

// The partner's reusable analytics reports. Authored once here, then pushed into selected
// client accounts where they run against that client's own data and report period.
export const PartnerLibraryAnalyticReportsPage = () => {
  const libraryAnalyticReportService = new LibraryAnalyticReportService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const navigate = useNavigate()

  const [reports, setReports] = useState<ILibraryAnalyticReport[]>()
  const [pushTarget, setPushTarget] = useState<{ id: string, name: string }>()
  const [deleteTarget, setDeleteTarget] = useState<{ id: string, name: string, pushedClientCount: number }>()

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(25)
  const [selected, setSelected] = useState<string[]>([]);
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()

  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  // Guards against a slow earlier request landing after a newer one.
  const requestId = useRef<number>(0)

  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  useEffect(() => {
    getAllReports()
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) setChange(change + 1)
      else setInputPage(0)
    }
    setHasPageMounted(true)
  }, [order, orderBy])

  useEffect(() => {
    if (inputPage === 0) setChange(c => c + 1)
    else setInputPage(0)
  }, [debouncedSearchQuery])

  const getAllReports = () => {
    requestId.current++
    const currentRequestId = requestId.current
    libraryAnalyticReportService.getAllWithPagination(inputPage, perPage, orderBy, order, debouncedSearchQuery || undefined)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
          setTotalRecords(response.count);
          setReports(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) setIsLoading(false)
        console.log(err)
      })
  }

  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'period', numeric: false, label: 'Period', sortable: false },
    { id: 'charts', numeric: false, label: 'Charts', sortable: false },
    { id: 'clients', numeric: false, label: 'Clients', sortable: false },
    { id: 'clientEditing', numeric: false, label: 'Client editing', sortable: false },
  ];

  const rows = !reports ? [] : reports.map((report) => {
    const charts = [
      report.pieChartCategory && `Pie: ${report.pieChartCategory}`,
      report.barChartCategory && `Bar: ${report.barChartCategory}`,
      report.stackedChartCategory && `Stacked: ${report.stackedChartCategory}`,
    ].filter(Boolean).join(' · ')

    return {
      id: report.id,
      name: report.name,
      period: report.usingCustomDates
        ? 'Custom dates'
        : (
          <Stack direction='row' alignItems='center' spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant='body2'>{report.reportPeriodName}</Typography>
            {report.periodFallbackClientCount > 0 &&
              <Chip size='small' variant='outlined' color='warning' label={`${report.periodFallbackClientCount} on a different period`} />}
          </Stack>
        ),
      charts: charts || 'None',
      clients: (
        <Stack direction='row' alignItems='center' spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant='body2'>
            {report.pushedClientCount === 0 ? 'Not pushed' : `${report.pushedClientCount} pushed`}
          </Typography>
          {report.outOfDateClientCount > 0 &&
            <Chip size='small' variant='outlined' color='warning' label={`${report.outOfDateClientCount} out of date`} />}
          {report.editedByClientCount > 0 &&
            <Chip size='small' variant='outlined' color='info' label={`${report.editedByClientCount} edited by client`} />}
          {report.noLongerPresentCount > 0 &&
            <Chip size='small' variant='outlined' color='error' label={`${report.noLongerPresentCount} no longer present`} />}
        </Stack>
      ),
      clientEditing: (
        <Chip
          size='small'
          variant='outlined'
          color={report.allowClientEdit ? 'default' : 'success'}
          icon={report.allowClientEdit ? <LockOpenOutlined fontSize='small' /> : <LockOutlined fontSize='small' />}
          label={report.allowClientEdit ? 'Client can edit' : 'Locked'}
        />
      ),
      //Pushing is the page's main per-row action, so it gets an inline button rather
      //than hiding in the kebab (same pattern as the reduction scenarios table).
      action: (
        <Stack direction='row' spacing={0.5} alignItems='center'>
          <Button
            size='small'
            startIcon={<PublishedWithChangesRounded />}
            onClick={() => setPushTarget({ id: report.id, name: report.name })}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Push to clients
          </Button>
          <TableActionButtons
            id={report.id}
            includeEdit={true}
            includeDelete={true}
            onEdit={() => navigate(`/partner-library/analytic-reports/edit-report/${report.id}`)}
            onDelete={() => setDeleteTarget({ id: report.id, name: report.name, pushedClientCount: report.pushedClientCount })}
            additionalMenuItems={[{
              label: report.allowClientEdit ? 'Lock for clients' : 'Allow clients to edit',
              icon: report.allowClientEdit
                ? <LockOutlined sx={{ cursor: 'pointer', mr: 1 }} />
                : <LockOpenOutlined sx={{ cursor: 'pointer', mr: 1 }} />,
              onClick: () => toggleClientEditHandler(report),
            }]}
          />
        </Stack>
      )
    }
  })

  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc') => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  // Turning client editing ON is harmless, so it applies straight away. Turning it OFF is
  // confirmed when clients have already changed their copies.
  const toggleClientEditHandler = (report: ILibraryAnalyticReport) => {
    const next = !report.allowClientEdit

    const apply = () => {
      libraryAnalyticReportService.setClientEditPermission(report.id, next)
        .then(() => {
          userNotificationsContext.openSnackbar({
            open: true,
            message: next
              ? 'Clients can now edit their copy of this report'
              : 'This report is now locked for clients',
          })
          getAllReports()
        })
        .catch(err => {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
          console.log(err)
        })
    }

    if (next || report.editedByClientCount === 0) {
      apply()
      return
    }

    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `${report.editedByClientCount} client${report.editedByClientCount === 1 ? ' has' : 's have'} already changed their copy of this report. Locking it stops them changing it further. Their current version stays in place until you push over it.`,
      buttonCopy: 'Lock it',
      cancelButtonCopy: 'Cancel',
      hideCancelButton: false
    })
      .then(response => { if (response) apply() })
      .catch(err => {
        userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' })
        console.log(err)
      })
  }

  return (
    <PageOuterContainer>
      <PageHeading
        title='Library'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => navigate('/partner-library/analytic-reports/add-new-report')}
        actionButtonText='Add Report'
        hasTabs={false}
      />
      <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
        Set up a report once here, then push it to the clients who need it. Each client's copy runs
        against their own data and their own report period.
      </Typography>
      <Box sx={{ mb: 2 }}>
        <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      </Box>
      <LoadingPage pageReady={!isLoading}>
        {reports && <TableDisplay
          onOrderChange={handleOrderChange}
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          selectByIcon={true}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />}
      </LoadingPage>

      {pushTarget &&
        <PartnerLibraryPushDialog
          open={Boolean(pushTarget)}
          libraryRuleId={pushTarget.id}
          libraryRuleName={pushTarget.name}
          resourceLabel='report'
          identityNoun='name'
          api={{
            getPushTargets: (id) => libraryAnalyticReportService.getPushTargets(id),
            push: (id, ids, overwriteIds) => libraryAnalyticReportService.push(id, ids, overwriteIds),
          }}
          renderClientDetail={(client) => {
            const detail = periodDetail(client)
            return detail
              ? <Typography variant='caption' sx={{ color: 'text.secondary' }}>{detail}</Typography>
              : null
          }}
          closeHandler={() => setPushTarget(undefined)}
          onPushed={getAllReports} />}

      {deleteTarget &&
        <PartnerLibraryDeleteDialog
          open={Boolean(deleteTarget)}
          libraryRuleId={deleteTarget.id}
          libraryRuleName={deleteTarget.name}
          pushedClientCount={deleteTarget.pushedClientCount}
          resourceLabel='report'
          inUseExplanation="A client who has this report wired into a scheduled send keeps their copy, because removing it would break that schedule."
          deleteResource={(id, removeFromClients) => libraryAnalyticReportService.delete(id, removeFromClients)}
          closeHandler={() => setDeleteTarget(undefined)}
          onDeleted={getAllReports} />}
    </PageOuterContainer>
  )
}
